// THE ACTIVE / INACTIVE INDICATOR on References and Rules (owner, 2026-09-11:
// "is there a cleaner way to indicate Active than the green circled Active text
// pill there now?").
//
// It was a Fluent `Badge appearance="outline" color="success|danger"` — a
// pill with a coloured ring around a coloured word. On a list whose default
// filter is Status: Active, that painted a green-ringed "Active" beside every
// single row, so the loudest repeated element on the page carried the least
// information: it says something only on the rows where it reads Inactive, and
// those are the rows the default view is hiding.
//
// A 6px dot and the plain word instead. The dot is the colour, the word is the
// state, and neither is a container — so a column of identical "Active" rows
// recedes the way a repeated value should, while an Inactive row still stands
// out because it is the only one that is not green. Red is kept for Inactive
// rather than softened to grey: a retired record is a deliberate state someone
// chose, and the row is already dimmed to 55% around it.
//
// NOT a Fluent Badge with `appearance="ghost"`, which is the nearest built-in:
// a ghost Badge still sizes itself as a pill (fixed height, horizontal padding,
// its own line box), so a column of them still reads as a column of chips.
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components'

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
    // THE ROW'S OWN SIZE AND COLOUR (owner, 2026-10-14: "make that Status text
    // use the same font size as the other texts in their rows"). It was the
    // smaller caption size in a softer grey, 12px beside 14px.
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  active: { backgroundColor: tokens.colorPaletteGreenForeground1 },
  inactive: { backgroundColor: tokens.colorPaletteRedForeground1 },
})

export function StatusDot({ active, label }: { active: boolean; label?: string }) {
  const s = useStyles()
  const text = label ?? (active ? 'Active' : 'Inactive')
  return (
    <span className={s.root}>
      {/* `aria-hidden`: the word beside it already carries the state, and a
          screen reader announcing a decorative dot would read it twice. */}
      <span className={mergeClasses(s.dot, active ? s.active : s.inactive)} aria-hidden="true" />
      {text}
    </span>
  )
}
